import { useState, useEffect } from 'react';
import { differenceInSeconds, parseISO } from 'date-fns';

export interface UseWorkoutTimerResult {
  elapsedTime: number; // in seconds
  formattedTime: string; // mm:ss format
  isRunning: boolean;
}

export interface UseWorkoutTimerOptions {
  startedAt?: Date | string | null;
  finishedAt?: Date | string | null;
}

/**
 * Hook to track elapsed time from workout startedAt timestamp.
 * Updates every second while the workout is in progress.
 */
export const useWorkoutTimer = ({
  startedAt,
  finishedAt,
}: UseWorkoutTimerOptions): UseWorkoutTimerResult => {
  const [elapsedTime, setElapsedTime] = useState(0);

  const isRunning = Boolean(startedAt && !finishedAt);

  useEffect(() => {
    if (!isRunning) {
      if (startedAt && finishedAt) {
        // Workout is finished, show total duration
        const start =
          typeof startedAt === 'string'
            ? parseISO(startedAt)
            : new Date(startedAt);
        const end =
          typeof finishedAt === 'string'
            ? parseISO(finishedAt)
            : new Date(finishedAt);
        const totalSeconds = differenceInSeconds(end, start);
        setElapsedTime(Math.max(0, totalSeconds));
      } else {
        // No workout started or invalid state
        setElapsedTime(0);
      }
      return;
    }

    // Calculate initial elapsed time
    const calculateElapsed = () => {
      const start =
        typeof startedAt === 'string'
          ? parseISO(startedAt)
          : new Date(startedAt!);
      const now = new Date();
      const seconds = differenceInSeconds(now, start);
      return Math.max(0, seconds);
    };

    setElapsedTime(calculateElapsed());

    // Set up interval to update every second
    const interval = setInterval(() => {
      setElapsedTime(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [startedAt, finishedAt, isRunning]);

  // Format time as mm:ss
  const formattedTime = formatTime(elapsedTime);

  return {
    elapsedTime,
    formattedTime,
    isRunning,
  };
};

/**
 * Formats seconds into mm:ss format
 */
function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
